import { BooLogo } from "@/components/BooLogo";
import { useEffect, useState } from "react";
import { MockupCard, PreviewShell } from "./PreviewShell";

type CardData = {
  channelLabel: string;
  channelMeta?: string;
  userInitial: string;
  userInitialBg: string;
  userName: string;
  userMessage: string;
  botMessage: string;
  source?: string;
};

type Scenario = {
  cardA: CardData;
  cardB: CardData;
  cardC: CardData;
};

const SCENARIO: Scenario = {
  cardA: {
    channelLabel: "direct message · Maya",
    userInitial: "M",
    userInitialBg: "bg-emerald-700",
    userName: "Maya",
    userMessage: "summarize today's standup in 2 lines",
    botMessage:
      "3 blockers, 2 resolved. Eng wants an extra QA day before Friday's release.",
  },
  cardB: {
    channelLabel: "# product-team",
    channelMeta: "3 members",
    userInitial: "JL",
    userInitialBg: "bg-indigo-700",
    userName: "Jamie",
    userMessage: "@boo what's the status of the Q3 launch plan?",
    botMessage:
      "Q3 launch is on track for Sept 18. Engineering finished the API work yesterday, design review wraps Friday. One blocker: legal sign-off on the ToS update.",
    source: "launch-plan.md, #engineering",
  },
  cardC: {
    channelLabel: "# revenue-ops · thread",
    userInitial: "S",
    userInitialBg: "bg-amber-700",
    userName: "Sam",
    userMessage: "margins SMB vs mid-market this quarter?",
    botMessage:
      "SMB margin up 8.4% — pricing change explains most of the lift. Mid-market flat at 31%.",
    source: "finance/q1-margins.md",
  },
};

export function Step3Preview() {
  const scenario = SCENARIO;

  const [showTyping, setShowTyping] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowTyping(false), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <PreviewShell>
      <div className="relative w-full">
        {/* Card A — peeks above */}
        <div className="boo-fade-in absolute right-0 top-0 -translate-y-[130%] w-[45%] rotate-3 origin-top-right z-10" style={{ animationDelay: "1500ms" }}>
          <SideCard data={scenario.cardA} />
        </div>

        {/* Card C — peeks below */}
        <div className="boo-fade-in absolute left-0 bottom-0 translate-y-[130%] w-[50%] -rotate-3 origin-bottom-left z-20" style={{ animationDelay: "2000ms" }}>
          <SideCard data={scenario.cardC} />
        </div>

        {/* Card B — main, foreground */}
        <div className="boo-fade-in relative -rotate-1 z-30" style={{ animationDelay: "200ms" }}>
          <MainCard data={scenario.cardB} showTyping={showTyping} />
        </div>
      </div>
    </PreviewShell>
  );
}

function MainCard({ data, showTyping }: { data: CardData; showTyping: boolean }) {
  return (
    <MockupCard>
      <div className="flex items-center gap-2 border-b border-stone-200 px-4 py-3">
        <span className="text-[13px] font-medium text-stone-900">
          {data.channelLabel}
        </span>
        {data.channelMeta && (
          <span className="ml-auto text-[11px] text-stone-500">{data.channelMeta}</span>
        )}
      </div>

      <div className="p-4">
        <div className="boo-fade-in mb-4 flex gap-2.5" style={{ animationDelay: "500ms" }}>
          <UserAvatar initial={data.userInitial} bg={data.userInitialBg} big />
          <div className="min-w-0 flex-1">
            <BooHeader name={data.userName} time="2:14 PM" big />
            <UserMessage text={data.userMessage} big />
          </div>
        </div>

        {showTyping && (
          <div className="boo-fade-in mb-4 flex gap-2.5" style={{ animationDelay: "1200ms" }}>
            <BooLogo className="size-8 rounded-md flex-shrink-0" />
            <div className="flex-1">
              <BooHeader name="Boo" app time="2:14 PM" big />
              <div className="flex items-center gap-1 py-2">
                <span className="boo-typing-dot h-1.5 w-1.5 rounded-full bg-stone-500" />
                <span
                  className="boo-typing-dot h-1.5 w-1.5 rounded-full bg-stone-500"
                  style={{ animationDelay: "200ms" }}
                />
                <span
                  className="boo-typing-dot h-1.5 w-1.5 rounded-full bg-stone-500"
                  style={{ animationDelay: "400ms" }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="boo-fade-in flex gap-2.5" style={{ animationDelay: "3200ms" }}>
          <BooLogo className="size-8 rounded-md flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <BooHeader name="Boo" app time="2:14 PM" big />
            <div className="text-[13px] leading-snug text-stone-900">{data.botMessage}</div>
            {data.source && (
              <div className="mt-2 rounded border-l-2 border-emerald-600 bg-stone-50 px-2.5 py-2 text-[11px] text-stone-500">
                Sourced from <span className="text-blue-700">{data.source}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </MockupCard>
  );
}

function SideCard({ data }: { data: CardData }) {
  return (
    <MockupCard>
      <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-2">
        <span className="text-[11px] text-stone-500">{data.channelLabel}</span>
      </div>
      <div className="px-3 py-2.5 space-y-2">
        <div className="flex gap-2">
          <UserAvatar initial={data.userInitial} bg={data.userInitialBg} />
          <div className="min-w-0 flex-1">
            <BooHeader name={data.userName} time="9:02 AM" />
            <UserMessage text={data.userMessage} />
          </div>
        </div>
        <div className="flex gap-2">
          <BooLogo className="size-6 rounded-md flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <BooHeader name="Boo" app time="9:02 AM" />
            <div className="text-[11px] leading-snug text-stone-900">{data.botMessage}</div>
            {data.source && (
              <div className="mt-1.5 rounded border-l-2 border-emerald-600 bg-stone-50 px-2 py-1.5 text-[10px] text-stone-500">
                Sourced from <span className="text-blue-700">{data.source}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </MockupCard>
  );
}

function UserAvatar({
  initial,
  bg,
  big,
}: {
  initial: string;
  bg: string;
  big?: boolean;
}) {
  const size = big ? "h-8 w-8" : "h-6 w-6";
  const text = big ? "text-xs" : "text-[9px]";
  return (
    <div
      className={`flex ${size} flex-shrink-0 items-center justify-center rounded-md ${bg} ${text} font-medium text-white`}
    >
      {initial}
    </div>
  );
}

function UserMessage({ text, big }: { text: string; big?: boolean }) {
  const cls = big ? "text-[13px] leading-snug" : "text-[11px] leading-snug";
  // Highlight @boo mentions in blue when present (org channel mention).
  const parts = text.split(/(@boo)/);
  return (
    <div className={`${cls} text-stone-900`}>
      {parts.map((p, i) =>
        p === "@boo" ? (
          <span key={i} className="text-blue-700">
            {p}
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </div>
  );
}

function BooHeader({
  name,
  time,
  app,
  big,
}: {
  name: string;
  time: string;
  app?: boolean;
  big?: boolean;
}) {
  const nameSize = big ? "text-[13px]" : "text-[11px]";
  const timeSize = big ? "text-[11px]" : "text-[9px]";
  const badgeSize = big ? "text-[9px]" : "text-[8px]";
  return (
    <div className="mb-0.5 flex items-baseline gap-1.5">
      <span className={`${nameSize} font-medium text-stone-900`}>{name}</span>
      {app && (
        <span
          className={`rounded-sm bg-indigo-50 px-1 py-px ${badgeSize} font-medium tracking-wide text-indigo-700`}
        >
          APP
        </span>
      )}
      <span className={`${timeSize} text-stone-500`}>{time}</span>
    </div>
  );
}
